import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Spinner } from "ui/core";

import { organisationSelectors } from "store/organisation/organisationSelectors";
import { organisationThunks } from "store/organisation/organisationThunks";
import { userThunks } from "store/user/userThunks";

import { PLACEHOLDER_ORG_ID } from "utilities/consts";
import { useAppDispatch } from "utilities/hooks/general";
import urls, { urlParts } from "utilities/urls";

const OrgRestrictedRoute = () => {
  const dispatch = useAppDispatch();
  const { orgId } = useParams();
  const currentOrgId = useSelector(organisationSelectors.currentOrganisationId);
  const navigate = useNavigate();
  const location = useLocation();

  //Make sure we find the default when the orgId is -1;
  useEffect(() => {
    if (orgId === PLACEHOLDER_ORG_ID) {
      if (currentOrgId) {
        const url = location.pathname.replace(
          `/${urlParts.org}/${PLACEHOLDER_ORG_ID}/`,
          `/${urlParts.org}/${currentOrgId}/`
        );
        navigate(url, { replace: true });
      } else {
        void dispatch(userThunks.fetchUserDetails()).unwrap();
      }
    }
  }, [currentOrgId, dispatch, location.pathname, navigate, orgId]);

  //Sync the selected organisation when the URL's orgId changes to a value
  //that isn't already selected (e.g. pasting/navigating to a link pointing
  //at a different organisation). We only act when the URL param itself has
  //changed since the last render (not merely when currentOrgId catches up
  //to a URL change we triggered ourselves, e.g. from the org switcher),
  //otherwise the two can race and briefly flip back to the previous org.
  const previousOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    const orgIdChanged = previousOrgIdRef.current !== orgId;
    previousOrgIdRef.current = orgId ?? null;

    if (
      orgIdChanged &&
      orgId &&
      orgId !== PLACEHOLDER_ORG_ID &&
      currentOrgId &&
      orgId !== currentOrgId
    ) {
      void dispatch(
        userThunks.updateCurrentOrganisation({ organisationId: orgId })
      )
        .unwrap()
        .catch(() => {
          // The URL points at an organisation we can't switch to (e.g. no
          // access, or an invalid id) - fall back to the current org instead
          // of leaving the user stuck on a mismatched URL.
          navigate(urls.default(), { replace: true });
        });
    }
  }, [orgId, currentOrgId, dispatch, navigate]);

  //Fetch the new organisation when it differs from the saved one or default one
  useEffect(() => {
    if (currentOrgId) {
      void dispatch(
        organisationThunks.getOrganisation({ orgId: currentOrgId })
      );
      void dispatch(
        organisationThunks.getReportingYears({ orgId: currentOrgId })
      );
    }
  }, [currentOrgId, dispatch]);

  // Wait for the store's selected org to actually match the URL before
  // rendering child routes, so pages don't fire requests against a stale
  // org while a switch (dropdown or URL-driven) is still in flight.
  const isOrgSynced =
    Boolean(orgId) && orgId !== PLACEHOLDER_ORG_ID && orgId === currentOrgId;

  return isOrgSynced ? <Outlet /> : <Spinner />;
};

export default OrgRestrictedRoute;
